import { useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useInteractionHandler } from '../../hooks/use-interaction-handler.js';
import { forward, unhandle, accept } from '../../utils/event.js';
import { isFocused } from '../../utils/focus.js';
import { isStandardShortcutKeyPressed } from '../../utils/modifiers.js';
import { selectInput } from './inputs.hooks.utils.js';

/**
 * Used to make an input "commit" on blur or enter, instead of calling `onChange` on every
 * keystroke. This allows deferring the change until the user is done typing, which can simplify
 * when the value needs to be updated.
 *
 * If the passed in `value` changes, the input will update to reflect the new value, unless the
 * input is currently being edited.
 *
 * This hook adds a `commit` function to the composable hook context, which can be used by other
 * hooks to force the current value to be sent through `onChange` and clear the editing status,
 * which is useful if another hook needs to be able to update the value even if the user has typed
 * something, like for arrow keys or increment/decrement buttons.
 *
 * - `Enter` - blur the input and trigger onChange
 * - `Escape` - reset back to the original value without triggering onChange
 */
const useCommitOnBlur = ({ ref, value: valueIn, onChange: onChangeIn, onInput, onKeyDown: onKeyDownIn, onBlur: onBlurIn, ...props }, shared) => {
    const editingRef = useRef(false);
    const recordedOnChange = useInteractionHandler(onChangeIn, { eventName: 'commit', recordingKey: shared?.recordingKey }, [onChangeIn]);
    const commit = useCallback(() => {
        const el = ref.current;
        if (el && isFocused(el)) {
            // Even if the input is unedited, commit should still have the side effect of selecting
            selectInput(el, shared);
        }
        if (!editingRef.current)
            return;
        const newValue = el.value;
        if (newValue !== valueIn) {
            // Reset the value back to what it was before the edit happened, and then let the incoming
            // value prop change update it back to the new value. This is to ensure that the input value
            // is always in sync with the valueIn prop, in case the caller ignores the onChange event.
            el.value = valueIn;
            recordedOnChange?.(newValue);
        }
        editingRef.current = false;
    }, [shared, recordedOnChange, ref, valueIn]);
    useEffect(() => {
        if (shared)
            shared.commit = commit;
    }, [shared, commit]);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!editingRef.current) {
            // While not editing, we allow ourselves to update the value on the dom input.
            el.value = valueIn ?? '';
            if (isFocused(el)) {
                selectInput(el, shared);
            }
        }
        // We expect `ref` to be stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueIn]);
    return {
        ...props,
        ref,
        defaultValue: valueIn,
        onKeyDown(e) {
            if (forward(e, onKeyDownIn))
                return;
            const el = e.currentTarget;
            switch (e.code) {
                case 'Enter':
                case 'NumpadEnter':
                    // TODO: is this a part of our input, or something handled by the properties panel?
                    // If not, then we need to extract a commit() function
                    el.blur();
                    break;
                case 'Escape':
                    // If the field is not being edited, then we don't consume this event. This allows building
                    // behavior where the first Escape resets the input, and the second Escape blurs it.
                    if (!editingRef.current)
                        return;
                    // Undo the changes made to the field, setting it back to the original value.
                    editingRef.current = false;
                    el.value = valueIn ?? '';
                    selectInput(el, shared);
                    // Accept the event because we handled it
                    break;
                case 'KeyZ':
                    if (isStandardShortcutKeyPressed(e) && !editingRef.current) {
                        // If we aren't editing, allow undo to be forwarded.
                        unhandle(e);
                    }
                    return;
                default:
                    return;
            }
            accept(e);
        },
        onBlur(e) {
            onBlurIn?.(e);
            commit();
        },
        onChange(...args) {
            editingRef.current = true;
            onInput?.(...args);
        },
    };
};

export { useCommitOnBlur };
//# sourceMappingURL=use-commit-on-blur.js.map
